import React, { Fragment, isValidElement, ReactElement, ReactNode } from 'react';
import { Variant, VariantProps, VariantComponent, VARIANT_BRAND } from './Variant';
import { useCommonAppServices } from '../../services/ContextProviders';

/** Utility to flatten Fragments/arrays and collect only valid React elements */
function flattenNodes(node: ReactNode, out: ReactElement[] = []): ReactElement[] {
  if (node == null || node === false) return out;

  if (Array.isArray(node)) {
    node.forEach((n) => flattenNodes(n, out));
    return out;
  }

  if (isValidElement(node)) {
    if (node.type === Fragment) {
      flattenNodes(node.props.children, out);
    } else {
      out.push(node as ReactElement);
    }
    return out;
  }

  out.push(node as ReactElement);
  return out;
}

function isVariantElement(el: ReactElement): el is ReactElement<VariantProps, VariantComponent> {
  const type = el.type as typeof Variant;
  return Boolean(type && type[VARIANT_BRAND] === true);
}

function getControlVariant(
  variants: Map<string, ReactElement<VariantProps, VariantComponent>>,
): ReactElement<VariantProps, VariantComponent> {
  const controlVariant = Array.from(variants.values()).find((variant) => variant.props.isControl);
  if (controlVariant) {
    return controlVariant;
  }
  return Array.from(variants.values())[0];
}

/**
 * Hook to programmatically get the selected variant name for an experiment
 */
export function useExperimentVariant(experimentName: string): string | null {
  const { experimentRegistry, featureFlagRegistry } = useCommonAppServices();
  return React.useMemo(() => {
    // Use global experiment registry to get experiment data
    const targetingParameters = experimentRegistry.getExperiment(experimentName);
    const skipExperiment = featureFlagRegistry.getFeatureFlag('skip_ab_test_experiment');
    if (!targetingParameters || skipExperiment === true) {
      return 'control'; // Return control variant name
    }

    const featureFlagForExperiment = featureFlagRegistry.getFeatureFlag(experimentName);
    if (featureFlagForExperiment) {
      return featureFlagForExperiment as string;
    }

    // Find the first variant that matches targeting parameters
    for (const key of Object.keys(targetingParameters)) {
      const isInExperiment = !!targetingParameters[key];
      if (isInExperiment) {
        return key;
      }
    }

    return 'control'; // Fallback to control
  }, [experimentRegistry, featureFlagRegistry, experimentName]);
}

export interface ExperimentProps {
  name: string;
  children: ReactNode;
}

export function Experiment({ name, children }: ExperimentProps) {
  const flat = flattenNodes(children);
  const selectedVariantName = useExperimentVariant(name);

  // Validate children: only <Variant> is allowed
  const variants: Map<string, ReactElement<VariantProps, VariantComponent>> = React.useMemo(() => {
    const variantOptions = new Map<string, ReactElement<VariantProps, VariantComponent>>();
    for (const el of flat) {
      if (!isValidElement(el)) {
        throw new Error(
          `Experiment: only <Variant> elements are allowed as children, found a non-element node.`,
        );
      }
      if (!isVariantElement(el)) {
        const bad =
          (el.type as typeof Variant)?.displayName ||
          (el.type as typeof Variant)?.name ||
          String(el.type);
        throw new Error(`Experiment: only <Variant> children are allowed. Found <${bad}>.`);
      }
      variantOptions.set(el.props.name, el);
    }

    if (variantOptions.size === 0) {
      throw new Error(`Experiment: requires at least one <Variant> child.`);
    }
    return variantOptions;
  }, [flat]);

  const selectedVariant: ReactElement = React.useMemo(() => {
    if (!selectedVariantName) {
      return getControlVariant(variants);
    }

    // Try to find the variant by name
    if (variants.has(selectedVariantName)) {
      return variants.get(selectedVariantName)!;
    }

    // Fallback to control variant
    return getControlVariant(variants);
  }, [selectedVariantName, variants]);

  return <React.Fragment>{selectedVariant}</React.Fragment>;
}
